import { useCallback, useRef, useState } from "react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import {
  ArrowUpDown,
  BarChart3,
  GripVertical,
  Layers,
  MoreVertical,
  Star,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TruncatedText } from "@/components/ui/truncated-text";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ThemeColumnCell } from "@/components/keyword-bank/KeywordThemeShared";
import { UtilizationCell, SourceCell } from "@/components/keyword-bank/TagCells";
import { KpsBreakdownPopover, keywordToKpsBreakdown } from "@/components/keyword-bank/KpsBreakdownPopover";
import { useKeywordRowActions } from "@/components/keyword-bank/useKeywordRowActions";
import { KEYWORD_COLUMN_TOOLTIPS as TT } from "@/components/keyword-bank/keywordColumnTooltips";
import { formatMetricNumber } from "@/utils/keywordMetricDisplay";
import {
  formatKeywordPendingNumber,
  formatKeywordPendingPercent,
  formatKeywordPendingSalesMoney,
} from "@/utils/keywordPendingDisplay";
import { KeywordMasterListAction } from "@/constants";
import { UtilizationTag } from "@/types";
import { HeroRankPill, HERO_RANK_TONE_CLASSES } from "./HeroRankPill";
import { HeroRankColorKey } from "@/components/shared/HeroRankColorKey";
import type { BankPickerRow, BankPickerSort, BankPickerSortKey } from "./select-bank-picker-rows";

export interface KeywordBankPickerTableProps {
  productId: number;
  /** The ASIN row actions (Add Landscape, Add to Rank Tracker) act against. */
  heroAsin: string;
  /** Already selected and sorted by `selectBankPickerRows` (WEB-004) -- this table does not
   *  filter, cap or sort on its own. */
  rows: BankPickerRow[];
  sort: BankPickerSort;
  onSortChange: (sort: BankPickerSort) => void;
  onPickKeyword: (row: BankPickerRow) => void;
  isApplying: boolean;
  stickyHeaderOffset: number;
  onOpenAddTheme: (keywordId: number) => void;
  /** The existing-themes catalog for the Theme cell's picker (*Threading*). */
  themes: { id: number; name: string }[];
}

const IDENTITY_MIN_WIDTH = 220;
const IDENTITY_MAX_WIDTH = 640;
const IDENTITY_DEFAULT_WIDTH = 316;
/** The keyword cell's non-text width: `px-3` both sides (24) + the star (16) + `gap-2` (8). */
const IDENTITY_FIXED_SEGMENTS_WIDTH = 48;

const COLUMN_COUNT = 13;

/** A click landing on any of these never counts as a row click (`sources.md § (c)`). */
const NON_ROW_CLICK_SELECTOR = "button, input, a, [role=checkbox], [role=menuitem]";

function nextSort(key: BankPickerSortKey, current: BankPickerSort): BankPickerSort {
  if (current === null || current.key !== key) return { key, dir: "desc" };
  if (current.dir === "desc") return { key, dir: "asc" };
  return null;
}

function isTracked(row: BankPickerRow): boolean {
  return row.source.utilizationTags?.includes(UtilizationTag.TRACKED) ?? false;
}

function SortIcon({ active }: { active: boolean }) {
  return <ArrowUpDown className={cn("h-3 w-3", active ? "text-primary" : "opacity-40")} />;
}

/**
 * `prototype-extracted.md § 4.7`, `sources.md` (AUT-457): every column header except Keywords and
 * Actions carries a definition hint on hover / keyboard focus, `signal-v3`'s `HeaderHint.tsx` --
 * whole-`<th>` hover, not just the label text. `TooltipTrigger asChild` wraps `TableHead` itself
 * (it's `forwardRef`, so Radix's Slot can attach its pointer/focus handlers straight onto the `<th>`)
 * rather than the label span, so hovering anywhere in the cell -- padding, sort icon included --
 * opens it. React's focus event bubbles through the vdom, so focusing the inner sort `<button>`
 * (for a sortable header) opens it too; a non-sortable header's label span carries its own
 * `tabIndex={0}` since it has no button ancestor to focus. Radix's own `onClick` (composed onto the
 * `<th>`, closes the tooltip) never intercepts the sort button's own `onClick` -- they're two
 * separate handlers on two different elements -- so a click still sorts exactly once.
 */
function ColumnHeader({
  tooltip,
  className,
  children,
}: {
  tooltip?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  const cell = <TableHead className={className}>{children}</TableHead>;
  if (!tooltip) return cell;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{cell}</TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-[280px] text-xs font-normal leading-relaxed text-left">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}

/** The dotted-underline visible label inside a `ColumnHeader`. `focusable` gives it its own
 *  `tabIndex`/focus ring for the static (non-sortable) headers, which have no button ancestor. */
function HeaderLabel({
  tooltip,
  focusable,
  children,
}: {
  tooltip?: ReactNode;
  focusable?: boolean;
  children: ReactNode;
}) {
  if (!tooltip) return <>{children}</>;
  return (
    <span
      tabIndex={focusable ? 0 : undefined}
      className={cn(
        "cursor-help border-b border-dotted border-muted-foreground",
        focusable && "outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      )}
    >
      {children}
    </span>
  );
}

/** Hero Rank's hint also carries the heat-scale legend (`sources.md § 4.7`). The rows and the band
 *  numbers come from the shared `HeroRankColorKey`; only the swatch colours are ours, read off
 *  `HeroRankPill` so the key and the cell can never disagree. The label text used to be written out
 *  here, which made it a second place the numbers could drift from. */
const HERO_RANK_TOOLTIP = (
  <div>
    <p>{TT.heroRank}</p>
    <HeroRankColorKey
      swatches={{ ...HERO_RANK_TONE_CLASSES, unranked: "bg-muted" }}
    />
  </div>
);

function SortableHeaderButton({
  label,
  sortKey,
  sort,
  onSortChange,
  className,
  tooltip,
}: {
  label: string;
  sortKey: BankPickerSortKey;
  sort: BankPickerSort;
  onSortChange: (sort: BankPickerSort) => void;
  className?: string;
  tooltip?: ReactNode;
}) {
  const active = sort?.key === sortKey;
  return (
    <ColumnHeader tooltip={tooltip} className={className}>
      <button
        type="button"
        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-foreground"
        onClick={() => onSortChange(nextSort(sortKey, sort))}
      >
        <HeaderLabel tooltip={tooltip}>{label}</HeaderLabel>
        <SortIcon active={active} />
      </button>
    </ColumnHeader>
  );
}

function renderTrend(trend: number | null) {
  if (trend === null) return <span className="text-muted-foreground text-xs">—</span>;
  const positive = trend >= 0;
  return (
    <span
      className={cn(
        "inline-flex justify-end gap-0.5 items-center text-xs tabular-nums",
        positive ? "text-[hsl(var(--trend-positive))]" : "text-destructive",
      )}
    >
      {positive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {Math.abs(trend)}%
    </span>
  );
}

/**
 * The Keywords tab's bank-mode keyword table (WEB-006). Rebuilt from
 * `components/ui/table` -- not the design's `KeywordTable.tsx`, and not the product
 * Keywords tab's `KeywordBankTable` (whose pagination, checkbox column and kebab
 * differ). Every row control (star, kebab, Theme cell) runs through
 * `useKeywordRowActions`; a row click applies the keyword via `onPickKeyword`.
 */
export function KeywordBankPickerTable({
  productId,
  heroAsin,
  rows,
  sort,
  onSortChange,
  onPickKeyword,
  isApplying,
  stickyHeaderOffset,
  onOpenAddTheme,
  themes,
}: KeywordBankPickerTableProps) {
  const { toggleMasterList, addLandscape, addToRankTracker, removeFromRankTracker, addToTheme } =
    useKeywordRowActions(productId);

  const [identityWidth, setIdentityWidth] = useState(IDENTITY_DEFAULT_WIDTH);
  const [resizing, setResizing] = useState(false);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  const onResizeStart = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      e.preventDefault();
      // AUT-456: the handle sits beside the Keywords sort button, not inside it, but stop the
      // event from bubbling anyway -- matching the design's `ColumnResizeHandle`, and cheap
      // insurance against a future layout change nesting the two.
      e.stopPropagation();
      // jsdom (unlike every real browser) has no Pointer Events capture API.
      e.currentTarget.setPointerCapture?.(e.pointerId);
      dragRef.current = { startX: e.clientX, startWidth: identityWidth };
      setResizing(true);
    },
    [identityWidth],
  );

  const onResizeMove = useCallback((e: React.PointerEvent<HTMLElement>) => {
    if (!dragRef.current) return;
    const delta = e.clientX - dragRef.current.startX;
    setIdentityWidth(
      Math.min(IDENTITY_MAX_WIDTH, Math.max(IDENTITY_MIN_WIDTH, dragRef.current.startWidth + delta)),
    );
  }, []);

  const onResizeEnd = useCallback((e: React.PointerEvent<HTMLElement>) => {
    dragRef.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    setResizing(false);
  }, []);

  const identityStyle = { width: identityWidth, minWidth: identityWidth };

  const handleRowClick = (e: React.MouseEvent<HTMLTableRowElement>, row: BankPickerRow) => {
    if (isApplying) return;
    // A portaled popover (KPS breakdown, "+N" tags) still bubbles its click through this row's
    // onClick, so check DOM containment before the selector-based check below.
    if (!e.currentTarget.contains(e.target as Node)) return;
    const target = e.target as HTMLElement;
    if (target.closest(NON_ROW_CLICK_SELECTOR)) return;
    onPickKeyword(row);
  };

  return (
    <Table stickyHeader stickyHeaderOffset={stickyHeaderOffset} className="min-w-[1200px]">
      <TableHeader>
        {/* AUT-457: one zero-delay provider for the header tooltips only, matching the product
            Keywords tab's `KeywordBankTableHeader.tsx` (also `delayDuration={0}`). Scoped to the
            header on purpose: body-cell tooltips (`TruncatedText`) keep the app provider's delay. */}
        <TooltipProvider delayDuration={0}>
        <TableRow>
          <TableHead className="sticky left-0 z-20 bg-card py-0 px-0" style={identityStyle}>
            <div className="relative flex min-w-0 items-center gap-2 px-3 py-2">
              <button
                type="button"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-foreground"
                onClick={() => onSortChange(nextSort("keyword", sort))}
              >
                Keywords
                <SortIcon active={sort?.key === "keyword"} />
              </button>
              <div
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize keyword column"
                onPointerDown={onResizeStart}
                onPointerMove={onResizeMove}
                onPointerUp={onResizeEnd}
                onPointerCancel={onResizeEnd}
                onClick={(e) => e.stopPropagation()}
                className="group/resize absolute right-0 top-0 bottom-0 z-30 flex w-4 cursor-col-resize touch-none select-none items-center justify-center"
              >
                {/* AUT-456: an always-visible divider rule, plus a grip that appears on hover / while
                    dragging -- `signal-v3`'s `ColumnResizeHandle`, rebuilt local to this table
                    (not extracted as shared structure; the task scope doesn't call for that). */}
                <span
                  aria-hidden
                  className={cn(
                    "absolute right-[7px] top-1 bottom-1 w-px rounded-full bg-border transition-colors",
                    "group-hover/resize:bg-primary",
                    resizing && "bg-primary",
                  )}
                />
                <GripVertical
                  aria-hidden
                  className={cn(
                    "relative h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity",
                    "group-hover/resize:opacity-100",
                    resizing ? "text-primary opacity-100" : "text-muted-foreground",
                  )}
                />
              </div>
            </div>
          </TableHead>
          <SortableHeaderButton label="Hero Rank" sortKey="rank" sort={sort} onSortChange={onSortChange} className="w-[90px]" tooltip={HERO_RANK_TOOLTIP} />
          <SortableHeaderButton label="KPS" sortKey="kps" sort={sort} onSortChange={onSortChange} className="w-[60px]" tooltip={TT.kps} />
          <ColumnHeader tooltip={TT.kwSales} className="w-[100px] px-2 text-right text-xs font-semibold text-foreground">
            <HeaderLabel tooltip={TT.kwSales} focusable>Kw. Sales</HeaderLabel>
          </ColumnHeader>
          <SortableHeaderButton label="Search Vol." sortKey="searchVolume" sort={sort} onSortChange={onSortChange} className="w-[90px]" tooltip={TT.searchVol} />
          <ColumnHeader tooltip={TT.asinDensity} className="w-[100px] px-2 text-right text-xs font-semibold text-foreground">
            <HeaderLabel tooltip={TT.asinDensity} focusable>ASIN Density</HeaderLabel>
          </ColumnHeader>
          <SortableHeaderButton label="Comp." sortKey="competition" sort={sort} onSortChange={onSortChange} className="w-[80px]" tooltip={TT.competition} />
          <SortableHeaderButton label="Rel." sortKey="relevance" sort={sort} onSortChange={onSortChange} className="w-[55px]" tooltip={TT.rel} />
          <SortableHeaderButton label="Trend" sortKey="trend" sort={sort} onSortChange={onSortChange} className="w-[55px]" tooltip={TT.trend} />
          <ColumnHeader tooltip={TT.theme} className="w-[150px] px-2 text-xs font-semibold text-foreground whitespace-nowrap">
            <HeaderLabel tooltip={TT.theme} focusable>Theme</HeaderLabel>
          </ColumnHeader>
          <ColumnHeader tooltip={TT.utilization} className="w-[160px] px-2 text-xs font-semibold text-foreground">
            <HeaderLabel tooltip={TT.utilization} focusable>Utilization</HeaderLabel>
          </ColumnHeader>
          <ColumnHeader tooltip={TT.source} className="w-[180px] px-2 text-xs font-semibold text-foreground">
            <HeaderLabel tooltip={TT.source} focusable>Source</HeaderLabel>
          </ColumnHeader>
          <TableHead className="w-[70px] px-2 text-center text-xs font-semibold text-foreground">Actions</TableHead>
        </TableRow>
        </TooltipProvider>
      </TableHeader>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={COLUMN_COUNT} className="p-8 text-center text-muted-foreground">
              No keywords match your search.
            </TableCell>
          </TableRow>
        ) : (
          rows.map((row) => {
            const tracked = isTracked(row);
            const relevanceDisplay =
              row.relevance === 0 ? "—" : formatKeywordPendingNumber(row.relevance);

            return (
              <TableRow
                key={row.keywordId}
                className="hover:bg-muted/20 cursor-pointer"
                onClick={(e) => handleRowClick(e, row)}
              >
                <TableCell className="sticky left-0 z-20 bg-card py-0 px-0" style={identityStyle}>
                  <div className="flex min-w-0 items-center gap-2 px-3 py-2 font-medium">
                    <button
                      type="button"
                      aria-label={row.isMaster ? "Remove from Master Keyword List" : "Add to Master Keyword List"}
                      className={cn(
                        "shrink-0 transition-colors",
                        row.isMaster
                          ? "text-amber-400 hover:text-amber-500"
                          : "text-muted-foreground/40 hover:text-amber-400",
                      )}
                      onClick={() =>
                        toggleMasterList(
                          row.keywordId,
                          row.isMaster ? KeywordMasterListAction.REMOVE : KeywordMasterListAction.ADD,
                        )
                      }
                    >
                      <Star className={cn("h-4 w-4", row.isMaster && "fill-current")} />
                    </button>
                    {/* AUT-456: an explicit width, as on the Keywords tab (`KeywordBankKeywordRow`).
                        Without one the cell's min-content is the whole keyword, so table layout
                        floors the column at the longest keyword and the drag cannot shrink it. */}
                    <TruncatedText width={identityWidth - IDENTITY_FIXED_SEGMENTS_WIDTH} className="min-w-0">
                      {row.keyword}
                    </TruncatedText>
                  </div>
                </TableCell>
                <TableCell className="w-[90px] px-0.5 py-1 text-center">
                  <HeroRankPill rank={row.rank} />
                </TableCell>
                <TableCell className="w-[60px] px-2 text-right font-medium">
                  <span className="inline-flex items-center justify-end gap-1">
                    {formatKeywordPendingNumber(row.kps)}
                    <KpsBreakdownPopover viewModel={keywordToKpsBreakdown(row.source)} />
                  </span>
                </TableCell>
                <TableCell className="w-[100px] px-2 text-right">
                  {formatKeywordPendingSalesMoney(row.source.keyword_sales)}
                </TableCell>
                <TableCell className="w-[90px] px-2 text-right">
                  {formatMetricNumber(row.searchVolume)}
                </TableCell>
                <TableCell className="w-[100px] px-2 text-right">
                  {formatKeywordPendingPercent(row.source.asin_density)}
                </TableCell>
                <TableCell className="w-[80px] px-2 text-right">
                  {formatMetricNumber(row.competition)}
                </TableCell>
                <TableCell className="w-[55px] px-2 text-right font-medium">
                  {relevanceDisplay}
                </TableCell>
                <TableCell className="w-[55px] px-2 text-right">{renderTrend(row.trend)}</TableCell>
                <TableCell className="w-[150px] px-2 text-xs whitespace-nowrap">
                  <ThemeColumnCell
                    themeName={row.theme ?? undefined}
                    themes={themes}
                    keywordId={row.keywordId}
                    onSelect={(themeId) => addToTheme(row.keywordId, themeId)}
                    onOpenAddTheme={() => onOpenAddTheme(row.keywordId)}
                  />
                </TableCell>
                <TableCell className="w-[160px] px-2">
                  <UtilizationCell keyword={row.source} align="start" />
                </TableCell>
                <TableCell className="w-[180px] px-2">
                  <SourceCell keyword={row.source} align="start" />
                </TableCell>
                <TableCell className="w-[70px] px-2 text-center">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-6 w-6" aria-label="Row actions">
                        <MoreVertical className="h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-popover z-50 rounded-md">
                      {!row.isMaster && (
                        <DropdownMenuItem
                          onClick={() => toggleMasterList(row.keywordId, KeywordMasterListAction.ADD)}
                        >
                          <Star className="h-4 w-4 mr-2" />
                          Add to Master Keyword List
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => addLandscape(row.keyword, heroAsin)}>
                        <Layers className="h-4 w-4 mr-2" />
                        Add Landscape
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() =>
                          tracked
                            ? removeFromRankTracker(row.source)
                            : addToRankTracker(row.source, heroAsin)
                        }
                      >
                        <BarChart3 className="h-4 w-4 mr-2" />
                        {tracked ? "Remove from Rank Tracker" : "Add to Rank Tracker"}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
